import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { YardDirection } from '@/api/generated.schemas';
import {
  DEFAULT_LOCALE,
  detailKey,
  DIRECTION_KEYS,
  FLOW_KEYS,
  formatShiftLabel,
  OTHER_VISITS_KEY,
  type YardMovesRow,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves.utils';
import {
  segmentStyle,
  type VesselNameById,
  type YardMovesPanel,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves-chart.utils';

const SIGNED_COUNT_FORMAT = new Intl.NumberFormat(DEFAULT_LOCALE, {
  signDisplay: 'exceptZero',
});
const VISIT_LIST_FORMAT = new Intl.ListFormat(DEFAULT_LOCALE, { type: 'unit' });

const END_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/** `Vessel name (visit id)`, the bare id without a name, or "Other visits". Shared with the filter bar. */
export function formatVisitLabel(
  t: TFunction,
  id: string,
  vesselName: string | null | undefined
): string {
  if (id === OTHER_VISITS_KEY) {
    return t('simulation.charts.yardMoves.otherVisits');
  }
  return vesselName
    ? t('simulation.charts.yardMoves.visitLabel', { name: vesselName, id })
    : id;
}

interface SimulationYardMovesTooltipProps {
  row: YardMovesRow;
  visitId: string;
  direction: YardDirection;
  timezone: string;
  panel: Pick<
    YardMovesPanel,
    'details' | 'groupedVisitIds' | 'highlightedVisitIds'
  >;
  shiftEndById: Record<string, string>;
  vesselNameById: VesselNameById;
}

/** Shift header + totals, then the hovered bar segment. */
export const SimulationYardMovesTooltip: FC<
  SimulationYardMovesTooltipProps
> = ({
  row,
  visitId,
  direction,
  timezone,
  panel: { details, groupedVisitIds, highlightedVisitIds },
  shiftEndById,
  vesselNameById,
}) => {
  const { t } = useTranslation();
  const count = row[DIRECTION_KEYS[direction]][visitId] ?? 0;
  const start = formatShiftLabel(row.date, timezone);
  const end = shiftEndById[row.shiftId];

  return (
    <div className='bg-background border-border/50 grid min-w-[12rem] gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl'>
      <div className='font-medium'>
        {end
          ? t('simulation.charts.yardMoves.tooltip.shift', {
              start,
              end: new Date(end).toLocaleTimeString(DEFAULT_LOCALE, {
                ...END_TIME_FORMAT,
                timeZone: timezone,
              }),
            })
          : t('simulation.charts.yardMoves.tooltip.shiftStart', { start })}
      </div>
      <div className='text-muted-foreground'>
        {t('simulation.charts.yardMoves.tooltip.totals', {
          in: row.inTotal,
          out: Math.abs(row.outTotal),
        })}
      </div>
      <div>
        <div className='flex items-center gap-1.5'>
          <span
            className='size-2 shrink-0 rounded-[2px]'
            style={{
              backgroundColor: segmentStyle(visitId, highlightedVisitIds).fill,
            }}
          />
          <span className='font-medium'>
            {t('simulation.charts.yardMoves.tooltip.segment', {
              visit: formatVisitLabel(t, visitId, vesselNameById[visitId]),
              direction: t(
                `simulation.charts.yardMoves.directions.${direction}`
              ),
              moves: SIGNED_COUNT_FORMAT.format(count),
            })}
          </span>
        </div>
        <ul className='text-muted-foreground pl-3.5'>
          {(details[detailKey(row.shiftId, visitId, direction)] ?? []).map(
            (detail) => (
              <li key={`${detail.container_flow}.${detail.transport_mode}`}>
                {t('simulation.charts.yardMoves.tooltip.detail', {
                  flow: t(FLOW_KEYS[detail.container_flow]),
                  mode: t(
                    `simulation.charts.yardMoves.transportModes.${detail.transport_mode}`
                  ),
                  moves: detail.container_count,
                })}
              </li>
            )
          )}
          {visitId === OTHER_VISITS_KEY && (
            <li>
              {t('simulation.charts.yardMoves.tooltip.groupedVisits', {
                total: groupedVisitIds.length,
                visits: VISIT_LIST_FORMAT.format(groupedVisitIds),
              })}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};
